import React, { useEffect, useContext, useState } from 'react'
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  OutlinedInput,
} from '@mui/material';

import Moment from 'moment'

import APIS from "../utils/APIS"

import UserContext from "../contexts/UserContext"
import LanguageContext from "../contexts/LanguageContext"
import { useTexte } from "../contexts/FormeAdresseContext"

import { logout } from "../utils/auth"


const OnBoarding = () => {
  const navigate = useNavigate();

  const { actLanguage, languageValues } = useContext(LanguageContext)
  /* Tutoiement / vouvoiement selon la cible du module (07/10/2026,
     contexts/FormeAdresseContext.js). */
  const t = useTexte()

  const {
    currentUser,
    updateUserContext,
  } = useContext(UserContext)

  return (
    <>
      <Helmet>
        <title>OnBoarding | TheLab</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.grey',
          minHeight: '100%',
          py: 0,
          display:"flex",
          flexDirection:"column",
        }}
      >
        <Box
          sx={{
            flex:1,
            padding:["20px", "50px", "50px", "80px"],
            backgroundColor:"background.grey",
            display:"flex",
            alignItems:"center",
          }}
        >
          <Container maxWidth="sm">
            <Formik
              initialValues={{
                sexe: currentUser?.sexe !== null ? currentUser.sexe : '',
                age: currentUser?.naissance ?  Moment(currentUser.naissance).format('YYYY-MM-DD') : '',
                poste: currentUser?.poste ? currentUser.poste : '',
              }}
              validationSchema={Yup.object().shape({
                sexe: Yup.string().max(255).required(`${languageValues?.requiredField[actLanguage]}`),
                age: Yup.string().max(255).required(`${languageValues?.requiredField[actLanguage]}`),
                poste : Yup.string().min(3, `${languageValues?.requiredField[actLanguage]}`).max(255).required(`${languageValues?.requiredField[actLanguage]}`),
              })}
              onSubmit={(values, { setSubmitting }) => {
                const userData = {
                  sexe: values?.sexe,
                  age : values?.age,
                  poste : values.poste,
                  departement : 99999,
                }
                APIS.onBoard(userData).then(result => {
                  if (result?.user) {
                    updateUserContext(null, true)
                    setSubmitting(false);
                  } else {
                    alert(languageValues?.erreurReessayer?.[actLanguage] || languageValues?.erreurReessayer?.fr)
                    setSubmitting(false)
                  }
                })
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
                touched,
                values
              }) => (
                <form onSubmit={handleSubmit}>
                  <Box sx={{ mb: 2, textAlign:"center" }}>
                    <Typography
                      color="text.blackTitle"
                      variant="h2"
                    >
                      {t(languageValues?.onBoarding?.title)}
                    </Typography>
                  </Box>
                  <Box sx={{display:"flex", flexDirection:["column", "row"], columnGap:"20px"}}>
                      <FormControl
                        fullWidth
                        margin="normal"
                        error={Boolean(touched.sexe && errors.sexe)}
                        variant="outlined"
                        sx={{
                          "& .MuiInputLabel-root" : {
                            color:"black !important"
                          }
                        }}
                      >
                        <InputLabel shrink>{languageValues?.onBoarding?.sexe[actLanguage]?.toUpperCase()}</InputLabel>
                        <Select
                          input={<OutlinedInput notched label={languageValues?.onBoarding?.sexe[actLanguage]?.toUpperCase()} />}
                          name="sexe"
                          value={values.sexe}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          placeholder="..."
                        >
                          {/* L70 : libelles traduits (etaient en dur en francais) */}
                          <MenuItem value={0}>{languageValues?.onBoarding?.sexeFemme[actLanguage]}</MenuItem>
                          <MenuItem value={1}>{languageValues?.onBoarding?.sexeHomme[actLanguage]}</MenuItem>
                        </Select>
                        <FormHelperText>{touched.sexe && errors.sexe}</FormHelperText>
                      </FormControl>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(touched.age && errors.age)}
                        fullWidth
                        helperText={touched.age && errors.age}
                        margin="normal"
                        name="age"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="date"
                        label={languageValues?.onBoarding?.age[actLanguage]?.toUpperCase()}
                        value={values.age}
                        variant="outlined"
                        sx={{
                          "& .MuiInputLabel-root" : {
                            color:"black !important"
                          },
                          "& input" : { textAlign:"left" }, // L72
                        }}
                      />
                  </Box>
                  <Box sx={{textTransform:"uppercase", fontSize:["12px", "15px"],paddingTop:"10px",}}>
                    {languageValues?.myAccount?.poste[actLanguage]}
                  </Box>
                  <TextField
                    error={Boolean(touched.poste && errors.poste)}
                    fullWidth
                    helperText={touched.poste && errors.poste}
                    margin="normal"
                    name="poste"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.poste}
                    variant="outlined"
                    placeholder="ex. RisingUp"
                  />
                  <Box sx={{ py: 2 }}>
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      {languageValues?.continue[actLanguage]}
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          </Container>
        </Box>
        {/* L73 : "Log-out - Besoin d'aide ? Clique ici" en bas de l'onboarding */}
        <Box sx={{
          padding:"20px 25px",
          display:"flex",
          justifyContent:"center",
          gap:"8px",
          fontSize:"15px",
          color:"text.subTitle",
          userSelect:"none",
        }}>
          <Box
            sx={{cursor:"pointer", textDecoration:"underline", textUnderlineOffset:"3px", "&:hover":{color:"text.black"}}}
            onClick={() => { logout(() => navigate("/login")) }}
          >
            {languageValues?.globalMenu?.logOut[actLanguage]}
          </Box>
          <Box>·</Box>
          <Box>{languageValues?.mainLayout?.needHelp[actLanguage]}</Box>
          <Box
            sx={{cursor:"pointer", textDecoration:"underline", textUnderlineOffset:"3px", "&:hover":{color:"text.black"}}}
            onClick={() => { window.location.href = "mailto:support@risinguparis.com" }}
          >
            {languageValues?.mainLayout?.itsHere[actLanguage]}
          </Box>
        </Box>
      </Box>
    </>
  )
};

export default OnBoarding;
